import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
    Activity,
    HeartPulse,
    ShieldCheck,
    FileText,
    AlertTriangle,
    TrendingUp,
    Heart,
    Sparkles,
    Calendar,
    Thermometer,
    Smile,
    Flame,
    Zap,
    Trophy,
    Timer,
    Moon,
    BedDouble,
    AlarmClock,
    Wind,
    RotateCw,
    CheckCircle2,
    Target,
    Award,
    Footprints,
    ArrowUpRight,
} from "lucide-react";

import aiAssistantFa from "./locales/AIAssistant-fa.json";
import aiAssistantEn from "./locales/AIAssistant-en.json";
import aiAssistantAr from "./locales/AIAssistant-ar.json";
import aiAssistantTr from "./locales/AIAssistant-tr.json";

// Health Smart AI SVG icon
const HealthSmartProductIcon = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M12 2L14.5 8.2L21 10.5L14.5 12.8L12 19L9.5 12.8L3 10.5L9.5 8.2L12 2Z"
            fill="currentColor"
        />
        <path
            d="M19 15L19.9 17.2L22 18L19.9 18.8L19 21L18.1 18.8L16 18L18.1 17.2L19 15Z"
            fill="currentColor"
            opacity="0.85"
        />
        <circle cx="6" cy="18" r="1.8" fill="currentColor" opacity="0.75" />
    </svg>
);

// Sleep Smart AI SVG icon
const SleepProductIcon = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z"
            fill="currentColor"
        />
        <path
            d="M17 3.5L17.6 4.9L19 5.5L17.6 6.1L17 7.5L16.4 6.1L15 5.5L16.4 4.9L17 3.5Z"
            fill="currentColor"
            opacity="0.85"
        />
    </svg>
);

// Women SVG icon from ProductIntro
const WomenProductIcon = (
    <svg width="22" height="22" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M7.28528 10.6329C7.93235 7.67959 9.3524 5.19058 11.7783 3.32313C12.2103 2.99054 12.603 3.39178 12.9262 3.66549C13.9023 4.49417 14.7409 5.47239 15.4106 6.56368C16.1363 7.7287 16.7743 9.29506 16.9946 10.6574C17.1875 10.5116 17.6221 10.3118 17.8538 10.2076C19.4624 9.48764 21.2109 9.13423 22.9729 9.17302C23.4876 9.18137 23.9803 9.07021 24.28 9.55173V10.7975C24.2176 10.9925 24.1349 11.8428 24.0839 12.1315C23.8793 13.2406 23.5171 14.3148 23.0087 15.3215C21.7615 17.8055 19.5679 19.6838 16.9214 20.5336C15.348 21.0448 13.8393 21.1281 12.205 21.0753C11.6753 21.0793 11.1458 21.099 10.6161 21.0796C8.58903 21.0077 6.66156 20.493 4.94624 19.3974C2.33016 17.7398 0.632783 14.9035 0.158162 11.8679C0.115952 11.598 0.0816709 11.0056 0 10.7755V9.5414C0.105387 9.37959 0.23677 9.2212 0.450005 9.20444C2.75727 9.02289 5.25524 9.48162 7.28528 10.6329Z"
            fill="currentColor"
        />
        <path
            d="M12.1403 4.19141C12.1829 4.20336 12.3698 4.36902 12.4127 4.40582C13.1055 5.00538 13.7217 5.68804 14.2474 6.43835C17.4628 11.0414 16.8772 16.8443 12.1534 20.1423C12.0821 20.1159 11.9157 19.9945 11.844 19.9414C9.07733 17.8895 7.62328 14.6235 8.11319 11.2035C8.44002 9.06151 9.38175 7.0605 10.824 5.44344C11.1853 5.03222 11.7007 4.52042 12.1403 4.19141Z"
            fill="currentColor"
            opacity="0.8"
        />
    </svg>
);

// Sport SVG icon from ProductIntro
const SportsProductIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.36362 11.6282C5.75362 11.0192 4.76562 11.0092 4.14262 11.6072C3.73862 11.9962 3.33862 12.3912 2.95262 12.7992C2.33762 13.4522 2.35162 14.3172 2.97662 14.9632C3.19103 15.1854 3.57067 15.5635 3.8091 15.7999C3.92729 15.9171 3.92787 16.108 3.81025 16.2258L3.17662 16.8602C2.63062 17.4052 2.63062 18.2902 3.17662 18.8362L5.16362 20.8232C5.70762 21.3682 6.59362 21.3682 7.13962 20.8232L7.77285 20.1884C7.89022 20.0708 8.08064 20.0707 8.19792 20.1885C8.42179 20.4132 8.7702 20.7623 8.97762 20.9672C9.66962 21.6462 10.4966 21.6832 11.2066 21.0382C11.6266 20.6572 12.0296 20.2552 12.4116 19.8352C12.9856 19.2012 12.9596 18.2252 12.3576 17.6202C10.3626 15.6192 8.36462 13.6222 6.36362 11.6282Z"
            fill="currentColor"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M20.9656 8.97798C20.7614 8.77058 20.4126 8.42263 20.1879 8.19906C20.0702 8.08187 20.0701 7.89152 20.1875 7.77405L20.8216 7.13998C21.3676 6.59398 21.3676 5.70898 20.8216 5.16398L18.8356 3.17698C18.2906 2.63098 17.4046 2.63098 16.8596 3.17698L16.2261 3.8105C16.1084 3.92818 15.9175 3.92772 15.8002 3.80962C15.5635 3.57119 15.1849 3.19144 14.9626 2.97698C14.3166 2.35198 13.4506 2.33798 12.7986 2.95398C12.3906 3.33898 11.9956 3.73798 11.6066 4.14298C11.0096 4.76598 11.0176 5.75298 11.6266 6.36398C13.6216 8.36498 15.6186 10.363 17.6196 12.357C18.2256 12.961 19.2006 12.986 19.8336 12.412C20.2546 12.031 20.6586 11.626 21.0386 11.207C21.6846 10.497 21.6466 9.66998 20.9656 8.97798Z"
            fill="currentColor"
            opacity="0.9"
        />
    </svg>
);

interface AssistantFeature {
    id: string;
    title: string;
    description: string;
    icon: React.ReactNode;
}

interface AssistantMetricCard {
    label: string;
    value: string;
    subtext: string;
    status: string;
    statusColor: string;
    progressPercent: number;
    icon: React.ReactNode;
}

interface AssistantType {
    id: "health" | "women" | "sport" | "sleep";
    title: string;
    shortTitle: string;
    tagline: string;
    icon: React.ReactNode;
    color: string;
    secondaryColor: string;
    gradient: string;
    bgIconGradient: string;
    accentColor: string;
    centerIconSvg: React.ReactNode;
    scoreTitle: string;
    scoreValue: string;
    scoreUnit: string;
    features: AssistantFeature[];
    backDetails: {
        summary: string;
        metrics: AssistantMetricCard[];
        aiInsight: string;
        actionLabel: string;
    };
}

const AIAssistantSection: React.FC = () => {
    const { t, i18n } = useTranslation("aiAssistant");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const [selectedAssistantId, setSelectedAssistantId] = useState<"health" | "women" | "sport" | "sleep">("health");
    const [activeFeatureId, setActiveFeatureId] = useState<string>("h1");
    const [isFlipped, setIsFlipped] = useState<boolean>(false);

    // Load translations
    useEffect(() => {
        const ns = "aiAssistant";
        const resources = {
            fa: aiAssistantFa,
            en: aiAssistantEn,
            ar: aiAssistantAr,
            tr: aiAssistantTr,
        };

        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const assistants: AssistantType[] = React.useMemo(() => {
        return [
            {
                id: "health",
                title: t("assistants.health.title", "دستیار جامع سلامت"),
                shortTitle: t("assistants.health.shortTitle", "دستیار سلامت"),
                tagline: t("assistants.health.tagline", "پایش ۲۴ ساعته علائم حیاتی، هشدار زودهنگام و گزارش‌های پیشرفته تحلیلی"),
                icon: HealthSmartProductIcon,
                color: "#155DFC",
                secondaryColor: "#3B82F6",
                gradient: "from-[#155DFC] via-[#0052CC] to-[#0038A8]",
                bgIconGradient: "linear-gradient(180deg, #E0EDFF 0%, rgba(224, 237, 255, 0.50) 100%)",
                accentColor: "#155DFC",
                scoreTitle: t("assistants.health.scoreTitle", "شاخص جامع سلامت زیستی"),
                scoreValue: t("assistants.health.scoreValue", "۹۴"),
                scoreUnit: t("assistants.health.scoreUnit", "از ۱۰۰"),
                centerIconSvg: (
                    <svg viewBox="0 0 48 48" className="w-16 h-16 sm:w-20 sm:h-20 text-white drop-shadow-md" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M24 4L28.8 15.2L40 20L28.8 24.8L24 36L19.2 24.8L8 20L19.2 15.2L24 4Z"
                            fill="currentColor"
                            fillOpacity="0.95"
                        />
                        <path
                            d="M37 29L39.2 34.8L45 37L39.2 39.2L37 45L34.8 39.2L29 37L34.8 34.8L37 29Z"
                            fill="#E0EDFF"
                        />
                        <circle cx="12" cy="36" r="3" fill="#E0EDFF" fillOpacity="0.85" />
                        <path
                            d="M6 36H18"
                            stroke="#E0EDFF"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                        />
                    </svg>
                ),
                features: [
                    {
                        id: "h1",
                        title: t("assistants.health.features.0.title", "پایش پیوسته ضربان قلب و آریتمی"),
                        description: t("assistants.health.features.0.description", "ثبت بی‌درنگ تغییرات ریتم قلب و اعلام فوری ناهنجاری‌ها و تپش نامنظم"),
                        icon: <Activity className="w-5 h-5" />,
                    },
                    {
                        id: "h2",
                        title: t("assistants.health.features.1.title", "سنجش سطح اکسیژن خون (SpO2)"),
                        description: t("assistants.health.features.1.description", "پایش پیوسته اکسیژن شریانی و شناسایی افت‌های احتمالی در طول شبانه‌روز"),
                        icon: <ShieldCheck className="w-5 h-5" />,
                    },
                    {
                        id: "h3",
                        title: t("assistants.health.features.2.title", "پایش استرس و تنفس هوشمند"),
                        description: t("assistants.health.features.2.description", "محاسبه شاخص استرس بر پایه نوسانات HRV و راهنمایی تمرینات تنفسی"),
                        icon: <TrendingUp className="w-5 h-5" />,
                    },
                    {
                        id: "h4",
                        title: t("assistants.health.features.3.title", "رصد دمای پوست و تب"),
                        description: t("assistants.health.features.3.description", "ثبت نوسانات میکرودمایی جهت آگاهی زودهنگام از تغییرات دمایی بدن"),
                        icon: <Thermometer className="w-5 h-5" />,
                    },
                    {
                        id: "h5",
                        title: t("assistants.health.features.4.title", "گزارش‌های تحلیلی و استاندارد"),
                        description: t("assistants.health.features.4.description", "تولید خلاصه هفتگی و ماهانه استاندارد با نمودارهای تخصصی جهت ارزیابی جامع"),
                        icon: <FileText className="w-5 h-5" />,
                    },
                    {
                        id: "h6",
                        title: t("assistants.health.features.5.title", "هشدار اضطراری و تشخیص سقوط"),
                        description: t("assistants.health.features.5.description", "تشخیص هوشمند شوک یا افتادن ناگهانی و ارسال هشدار فوری به مراقبان"),
                        icon: <AlertTriangle className="w-5 h-5" />,
                    },
                ],
                backDetails: {
                    summary: t("assistants.health.backDetails.summary", "سیستم تحلیل هوش مصنوعی دلبند پارامترهای زیستی شما را به صورت لحظه‌ای بررسی و ریتم قلبی و استرس را پایدار نگه می‌دارد."),
                    metrics: [
                        {
                            label: t("assistants.health.backDetails.metrics.0.label", "ضربان و نوسان (HRV)"),
                            value: t("assistants.health.backDetails.metrics.0.value", "۷۲ BPM | ۶۸ms"),
                            subtext: t("assistants.health.backDetails.metrics.0.subtext", "پاسخ عالی سیستم سمپاتیک"),
                            status: t("assistants.health.backDetails.metrics.0.status", "طبیعی و بهینه"),
                            statusColor: "#155DFC",
                            progressPercent: 88,
                            icon: <HeartPulse className="w-4 h-4 text-[#155DFC]" />,
                        },
                        {
                            label: t("assistants.health.backDetails.metrics.1.label", "اکسیژن شریانی (SpO2)"),
                            value: t("assistants.health.backDetails.metrics.1.value", "۹۸.۵ ٪"),
                            subtext: t("assistants.health.backDetails.metrics.1.subtext", "اکسیژن‌رسانی بهینه بافت‌ها"),
                            status: t("assistants.health.backDetails.metrics.1.status", "سطح عالی"),
                            statusColor: "#0284C7",
                            progressPercent: 98,
                            icon: <ShieldCheck className="w-4 h-4 text-cyan-600" />,
                        },
                        {
                            label: t("assistants.health.backDetails.metrics.2.label", "شاخص استرس عصبی"),
                            value: t("assistants.health.backDetails.metrics.2.value", "۲۴ ٪ (پایین)"),
                            subtext: t("assistants.health.backDetails.metrics.2.subtext", "تعادل مناسب بین کار و استراحت"),
                            status: t("assistants.health.backDetails.metrics.2.status", "آرامش کامل"),
                            statusColor: "#10B981",
                            progressPercent: 24,
                            icon: <Smile className="w-4 h-4 text-emerald-600" />,
                        },
                        {
                            label: t("assistants.health.backDetails.metrics.3.label", "دمای پایه پوست"),
                            value: t("assistants.health.backDetails.metrics.3.value", "۳۶.۶ °C"),
                            subtext: t("assistants.health.backDetails.metrics.3.subtext", "بدون علائم تب یا نوسان دمایی"),
                            status: t("assistants.health.backDetails.metrics.3.status", "پایدار"),
                            statusColor: "#6366F1",
                            progressPercent: 75,
                            icon: <Thermometer className="w-4 h-4 text-indigo-600" />,
                        },
                    ],
                    aiInsight: t("assistants.health.backDetails.aiInsight", "ریتم قلبی و سطح اکسیژن در ایده‌آل‌ترین وضعیت قرار دارند. برای حفظ پایداری HRV، روزانه ۲۰ دقیقه پیاده‌روی سبک ادامه یابد."),
                    actionLabel: t("assistants.health.backDetails.actionLabel", "دریافت پرونده کامل شاخص‌های زیستی"),
                },
            },
            {
                id: "women",
                title: t("assistants.women.title", "دستیار هوشمند بانوان"),
                shortTitle: t("assistants.women.shortTitle", "دستیار بانوان"),
                tagline: t("assistants.women.tagline", "پیش‌بینی هوشمند چرخه ماهانه، فازهای باروری، علائم PMS و نوسانات هورمونی"),
                icon: WomenProductIcon,
                color: "#FF6F8F",
                secondaryColor: "#FFA6B9",
                gradient: "from-[#FF8EA6] via-[#FF6F8F] to-[#E85678]",
                bgIconGradient: "linear-gradient(180deg, #FFF0F3 0%, rgba(255, 240, 243, 0.50) 100%)",
                accentColor: "#FF6F8F",
                scoreTitle: t("assistants.women.scoreTitle", "نمره تعادل و سلامت هورمونی"),
                scoreValue: t("assistants.women.scoreValue", "۹۶"),
                scoreUnit: t("assistants.women.scoreUnit", "٪ پایداری"),
                centerIconSvg: (
                    <svg viewBox="0 0 25 25" className="w-16 h-16 sm:w-20 sm:h-20 text-white drop-shadow-md" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M7.28528 10.6329C7.93235 7.67959 9.3524 5.19058 11.7783 3.32313C12.2103 2.99054 12.603 3.39178 12.9262 3.66549C13.9023 4.49417 14.7409 5.47239 15.4106 6.56368C16.1363 7.7287 16.7743 9.29506 16.9946 10.6574C17.1875 10.5116 17.6221 10.3118 17.8538 10.2076C19.4624 9.48764 21.2109 9.13423 22.9729 9.17302C23.4876 9.18137 23.9803 9.07021 24.28 9.55173V10.7975C24.2176 10.9925 24.1349 11.8428 24.0839 12.1315C23.8793 13.2406 23.5171 14.3148 23.0087 15.3215C21.7615 17.8055 19.5679 19.6838 16.9214 20.5336C15.348 21.0448 13.8393 21.1281 12.205 21.0753C11.6753 21.0793 11.1458 21.099 10.6161 21.0796C8.58903 21.0077 6.66156 20.493 4.94624 19.3974C2.33016 17.7398 0.632783 14.9035 0.158162 11.8679C0.115952 11.598 0.0816709 11.0056 0 10.7755V9.5414C0.105387 9.37959 0.23677 9.2212 0.450005 9.20444C2.75727 9.02289 5.25524 9.48162 7.28528 10.6329Z"
                            fill="currentColor"
                            fillOpacity="0.95"
                        />
                        <path
                            d="M12.1403 4.19141C12.1829 4.20336 12.3698 4.36902 12.4127 4.40582C13.1055 5.00538 13.7217 5.68804 14.2474 6.43835C17.4628 11.0414 16.8772 16.8443 12.1534 20.1423C12.0821 20.1159 11.9157 19.9945 11.844 19.9414C9.07733 17.8895 7.62328 14.6235 8.11319 11.2035C8.44002 9.06151 9.38175 7.0605 10.824 5.44344C11.1853 5.03222 11.7007 4.52042 12.1403 4.19141Z"
                            fill="#FFE4E8"
                        />
                        <path
                            d="M22.1025 10.0875C22.5332 10.0601 22.9588 10.0711 23.3897 10.082C23.241 15.3077 20.0063 19.3912 14.7302 20.0987C14.332 20.1441 13.9542 20.1654 13.5569 20.2007C14.0116 19.7929 14.393 19.4349 14.7899 18.9647C16.5318 16.9235 17.3708 14.2626 17.1145 11.5914C17.4351 11.4531 17.8036 11.2268 18.1503 11.0705C19.3936 10.4957 20.7348 10.1622 22.1025 10.0875Z"
                            fill="#FFF0F3"
                            fillOpacity="0.85"
                        />
                        <path
                            d="M1.45902 10.0648C2.9954 10.052 4.51719 10.3636 5.92491 10.9792C6.31277 11.1457 6.84542 11.4614 7.16964 11.581C7.11375 12.0233 7.11283 12.5268 7.13234 12.9708C7.23046 15.1869 8.07021 17.3058 9.51683 18.9874C9.92272 19.4638 10.237 19.7586 10.6979 20.1806C10.5503 20.1793 10.377 20.1659 10.2276 20.1579C7.84099 20.0293 5.59922 19.1063 3.89215 17.4207C1.98023 15.5327 0.915818 12.7448 0.895996 10.0755L1.45902 10.0648Z"
                            fill="#FFF0F3"
                            fillOpacity="0.85"
                        />
                    </svg>
                ),
                features: [
                    {
                        id: "w1",
                        title: t("assistants.women.features.0.title", "پیش‌بینی دقیق شروع سیکل ماهانه"),
                        description: t("assistants.women.features.0.description", "محاسبه هوشمند بر مبنای سوابق قبلی، نوسانات دمای پوست و الگوریتم‌های هوش مصنوعی"),
                        icon: <Calendar className="w-5 h-5" />,
                    },
                    {
                        id: "w2",
                        title: t("assistants.women.features.1.title", "تشخیص پنجره باروری و تخمک‌گذاری"),
                        description: t("assistants.women.features.1.description", "ثبت و تحلیل دمای پایه بدن (BBT) برای تخمین دقیق روزهای با شانس باروری بالا"),
                        icon: <Sparkles className="w-5 h-5" />,
                    },
                    {
                        id: "w3",
                        title: t("assistants.women.features.2.title", "پایش سندروم پیش از قاعدگی (PMS)"),
                        description: t("assistants.women.features.2.description", "ثبت روزانه علائم خلقی و فیزیکی و ارائه توصیه‌های تغذیه‌ای و تمرینی آرام‌بخش"),
                        icon: <Heart className="w-5 h-5" />,
                    },
                    {
                        id: "w4",
                        title: t("assistants.women.features.3.title", "ردیابی تغییرات دمایی هورمونی"),
                        description: t("assistants.women.features.3.description", "سنجش شیب نوسان دمای بدن حین خواب جهت شناسایی دقیق تغییرات فاز لوتئال"),
                        icon: <Thermometer className="w-5 h-5" />,
                    },
                    {
                        id: "w5",
                        title: t("assistants.women.features.4.title", "مدیریت و پایش دوران بارداری"),
                        description: t("assistants.women.features.4.description", "ثبت علائم حیاتی مادر و ارائه راهنماهای خودمراقبتی متناسب با هفته بارداری"),
                        icon: <Smile className="w-5 h-5" />,
                    },
                    {
                        id: "w6",
                        title: t("assistants.women.features.5.title", "یادآورهای اختصاصی مکمل و آب"),
                        description: t("assistants.women.features.5.description", "تنظیم هشدار هوشمند مصرف آهن، فولیک اسید و هیدراتاسیون بدن"),
                        icon: <CheckCircle2 className="w-5 h-5" />,
                    },
                ],
                backDetails: {
                    summary: t("assistants.women.backDetails.summary", "دستیار بانوان با تلفیق داده‌های فیزیولوژیک و یادگیری ماشین، نقشه جامع سلامت هورمونی و پیش‌بینی چرخه را ترسیم می‌کند."),
                    metrics: [
                        {
                            label: t("assistants.women.backDetails.metrics.0.label", "فاز سیکل و زمان‌بندی"),
                            value: t("assistants.women.backDetails.metrics.0.value", "روز ۱۲ (فولیکولار)"),
                            subtext: t("assistants.women.backDetails.metrics.0.subtext", "انرژی و تمرکز ذهنی در اوج"),
                            status: t("assistants.women.backDetails.metrics.0.status", "انرژی بالا"),
                            statusColor: "#E11D48",
                            progressPercent: 42,
                            icon: <Sparkles className="w-4 h-4 text-rose-500" />,
                        },
                        {
                            label: t("assistants.women.backDetails.metrics.1.label", "پنجره تخمک‌گذاری"),
                            value: t("assistants.women.backDetails.metrics.1.value", "۳ روز دیگر"),
                            subtext: t("assistants.women.backDetails.metrics.1.subtext", "شانس باروری: متوسط رو به بالا"),
                            status: t("assistants.women.backDetails.metrics.1.status", "فاز باروری"),
                            statusColor: "#F43F5E",
                            progressPercent: 78,
                            icon: <Calendar className="w-4 h-4 text-pink-500" />,
                        },
                        {
                            label: t("assistants.women.backDetails.metrics.2.label", "دمای پایه بدن (BBT)"),
                            value: t("assistants.women.backDetails.metrics.2.value", "۳۶.۴۲ °C"),
                            subtext: t("assistants.women.backDetails.metrics.2.subtext", "شیب ورود به مرحله لوتئال"),
                            status: t("assistants.women.backDetails.metrics.2.status", "الگوی استاندارد"),
                            statusColor: "#FB7185",
                            progressPercent: 65,
                            icon: <Thermometer className="w-4 h-4 text-rose-400" />,
                        },
                        {
                            label: t("assistants.women.backDetails.metrics.3.label", "کیفیت خواب و انرژی"),
                            value: t("assistants.women.backDetails.metrics.3.value", "۸۹ ٪ رضایت‌بخش"),
                            subtext: t("assistants.women.backDetails.metrics.3.subtext", "تعادل مطلوب دمایی در شب"),
                            status: t("assistants.women.backDetails.metrics.3.status", "استراحت عالی"),
                            statusColor: "#EC4899",
                            progressPercent: 89,
                            icon: <Heart className="w-4 h-4 text-pink-600" />,
                        },
                    ],
                    aiInsight: t("assistants.women.backDetails.aiInsight", "ورود به فاز تخمک‌گذاری نزدیک است؛ هوش مصنوعی توصیه می‌کند مصرف مایعات و منیزیم را افزایش دهید."),
                    actionLabel: t("assistants.women.backDetails.actionLabel", "مشاهده تقویم و چرخه هورمونی"),
                },
            },
            {
                id: "sport",
                title: t("assistants.sport.title", "دستیار تخصصی ورزش و تناسب‌اندام"),
                shortTitle: t("assistants.sport.shortTitle", "دستیار ورزش"),
                tagline: t("assistants.sport.tagline", "برنامه‌ریزی هوشمند تمرین، سنجش توان قلبی-عروقی، کالری‌سنجی دقیق و زمان ریکاوری"),
                icon: SportsProductIcon,
                color: "#4DCE7C",
                secondaryColor: "#10B981",
                gradient: "from-[#4DCE7C] via-[#22C55E] to-[#10B981]",
                bgIconGradient: "linear-gradient(180deg, #E6FDF0 0%, rgba(230, 253, 240, 0.50) 100%)",
                accentColor: "#10B981",
                scoreTitle: t("assistants.sport.scoreTitle", "شاخص آمادگی تمرین و توان بدنی"),
                scoreValue: t("assistants.sport.scoreValue", "۹۲"),
                scoreUnit: t("assistants.sport.scoreUnit", "٪ اوج آمادگی"),
                centerIconSvg: (
                    <svg viewBox="0 0 24 24" className="w-16 h-16 sm:w-20 sm:h-20 text-white drop-shadow-md" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M6.36362 11.6282C5.75362 11.0192 4.76562 11.0092 4.14262 11.6072C3.73862 11.9962 3.33862 12.3912 2.95262 12.7992C2.33762 13.4522 2.35162 14.3172 2.97662 14.9632C3.19103 15.1854 3.57067 15.5635 3.8091 15.7999C3.92729 15.9171 3.92787 16.108 3.81025 16.2258L3.17662 16.8602C2.63062 17.4052 2.63062 18.2902 3.17662 18.8362L5.16362 20.8232C5.70762 21.3682 6.59362 21.3682 7.13962 20.8232L7.77285 20.1884C7.89022 20.0708 8.08064 20.0707 8.19792 20.1885C8.42179 20.4132 8.7702 20.7623 8.97762 20.9672C9.66962 21.6462 10.4966 21.6832 11.2066 21.0382C11.6266 20.6572 12.0296 20.2552 12.4116 19.8352C12.9856 19.2012 12.9596 18.2252 12.3576 17.6202C10.3626 15.6192 8.36462 13.6222 6.36362 11.6282Z"
                            fill="currentColor"
                            fillOpacity="0.95"
                        />
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M20.9656 8.97798C20.7614 8.77058 20.4126 8.42263 20.1879 8.19906C20.0702 8.08187 20.0701 7.89152 20.1875 7.77405L20.8216 7.13998C21.3676 6.59398 21.3676 5.70898 20.8216 5.16398L18.8356 3.17698C18.2906 2.63098 17.4046 2.63098 16.8596 3.17698L16.2261 3.8105C16.1084 3.92818 15.9175 3.92772 15.8002 3.80962C15.5635 3.57119 15.1849 3.19144 14.9626 2.97698C14.3166 2.35198 13.4506 2.33798 12.7986 2.95398C12.3906 3.33898 11.9956 3.73798 11.6066 4.14298C11.0096 4.76598 11.0176 5.75298 11.6266 6.36398C13.6216 8.36498 15.6186 10.363 17.6196 12.357C18.2256 12.961 19.2006 12.986 19.8336 12.412C20.2546 12.031 20.6586 11.626 21.0386 11.207C21.6846 10.497 21.6466 9.66998 20.9656 8.97798Z"
                            fill="#E6FDF0"
                            fillOpacity="0.85"
                        />
                    </svg>
                ),
                features: [
                    {
                        id: "s1",
                        title: t("assistants.sport.features.0.title", "شناسایی خودکار بیش از ۵۰ حالت ورزشی"),
                        description: t("assistants.sport.features.0.description", "تشخیص دقیق دویدن، شنا، دوچرخه‌سواری، بدنسازی و ورزش‌های هوازی بدون نیاز به تنظیم دستی"),
                        icon: <Trophy className="w-5 h-5" />,
                    },
                    {
                        id: "s2",
                        title: t("assistants.sport.features.1.title", "محاسبه شاخص آمادگی هوازی (VO2 Max)"),
                        description: t("assistants.sport.features.1.description", "سنجش حداکثر توان جذب اکسیژن برای پایش پیشرفت ظرفیت استقامتی ورزشکار"),
                        icon: <Zap className="w-5 h-5" />,
                    },
                    {
                        id: "s3",
                        title: t("assistants.sport.features.2.title", "تخمین زمان ریکاوری و بازسازی عضلانی"),
                        description: t("assistants.sport.features.2.description", "محاسبه مدت زمان مورد نیاز استراحت پیش از جلسه تمرینی سنگین بعدی"),
                        icon: <Timer className="w-5 h-5" />,
                    },
                    {
                        id: "s4",
                        title: t("assistants.sport.features.3.title", "تفکیک زون‌های ضربان قلب تمرینی"),
                        description: t("assistants.sport.features.3.description", "آنالیز زمان سپری شده در زون‌های چربی‌سوزی، هوازی و آستانه بی‌هوازی"),
                        icon: <HeartPulse className="w-5 h-5" />,
                    },
                    {
                        id: "s5",
                        title: t("assistants.sport.features.4.title", "کالری‌شمار هوشمند بر مبنای توان واقعی"),
                        description: t("assistants.sport.features.4.description", "محاسبه دقیق مصرف انرژی با در نظر گرفتن سن، وزن، ضربان و شیب حرکت"),
                        icon: <Flame className="w-5 h-5" />,
                    },
                    {
                        id: "s6",
                        title: t("assistants.sport.features.5.title", "آنالیز بار تمرینی و جلوگیری از بیش‌تمرینی"),
                        description: t("assistants.sport.features.5.description", "هشدار خستگی مفرط سیستم عضلانی-عصبی و پیشگیری از آسیب‌دیدگی"),
                        icon: <CheckCircle2 className="w-5 h-5" />,
                    },
                ],
                backDetails: {
                    summary: t("assistants.sport.backDetails.summary", "دستیار ورزشی دلبند با تحلیل داده‌های حرکتی و شدت فعالیت، توان فیزیکی شما را ارتقا داده و از آسیب پیشگیری می‌کند."),
                    metrics: [
                        {
                            label: t("assistants.sport.backDetails.metrics.0.label", "شاخص آمادگی هوازی (VO2 Max)"),
                            value: t("assistants.sport.backDetails.metrics.0.value", "۵۲.۴ ml/kg/min"),
                            subtext: t("assistants.sport.backDetails.metrics.0.subtext", "سطح عالی برای گروه سنی"),
                            status: t("assistants.sport.backDetails.metrics.0.status", "عالی"),
                            statusColor: "#059669",
                            progressPercent: 92,
                            icon: <Zap className="w-4 h-4 text-green-600" />,
                        },
                        {
                            label: t("assistants.sport.backDetails.metrics.1.label", "کالری فعال سوزانده‌شده"),
                            value: t("assistants.sport.backDetails.metrics.1.value", "۶۴۰ kcal"),
                            subtext: t("assistants.sport.backDetails.metrics.1.subtext", "۸۰٪ از هدف روزانه ۶۸۰ kcal"),
                            status: t("assistants.sport.backDetails.metrics.1.status", "پیشرفت عالی"),
                            statusColor: "#10B981",
                            progressPercent: 80,
                            icon: <Flame className="w-4 h-4 text-emerald-600" />,
                        },
                        {
                            label: t("assistants.sport.backDetails.metrics.2.label", "زمان ریکاوری مورد نیاز"),
                            value: t("assistants.sport.backDetails.metrics.2.value", "۱۴ ساعت باقی‌مانده"),
                            subtext: t("assistants.sport.backDetails.metrics.2.subtext", "تا جلسه بعدی تمرینات با شدت بالا"),
                            status: t("assistants.sport.backDetails.metrics.2.status", "ریکاوری بهینه"),
                            statusColor: "#34D399",
                            progressPercent: 60,
                            icon: <Timer className="w-4 h-4 text-emerald-500" />,
                        },
                        {
                            label: t("assistants.sport.backDetails.metrics.3.label", "تعداد گام‌ها و مسافت"),
                            value: t("assistants.sport.backDetails.metrics.3.value", "۸,۴۲۰ گام | ۶.۲ km"),
                            subtext: t("assistants.sport.backDetails.metrics.3.subtext", "فعالیت روزانه فراتر از میانگین"),
                            status: t("assistants.sport.backDetails.metrics.3.status", "هدف محقق شد"),
                            statusColor: "#047857",
                            progressPercent: 90,
                            icon: <Footprints className="w-4 h-4 text-teal-600" />,
                        },
                    ],
                    aiInsight: t("assistants.sport.backDetails.aiInsight", "بدن شما در وضعیت اوج آمادگی عصبی-عضلانی قرار دارد؛ امروز بهترین زمان برای یک جلسه تمرین پرانرژی است."),
                    actionLabel: t("assistants.sport.backDetails.actionLabel", "آنالیز تاریخچه عملکرد ورزشی"),
                },
            },
            {
                id: "sleep",
                title: t("assistants.sleep.title", "دستیار پیشرفته آنالیز خواب"),
                shortTitle: t("assistants.sleep.shortTitle", "دستیار خواب"),
                tagline: t("assistants.sleep.tagline", "تفکیک فازهای خواب عمیق و REM، سنجش نظم تنفس و کیفیت استراحت با نمره هوشمند"),
                icon: SleepProductIcon,
                color: "#6366F1",
                secondaryColor: "#818CF8",
                gradient: "from-[#6366F1] via-[#4F46E5] to-[#4338CA]",
                bgIconGradient: "linear-gradient(180deg, #EEF2FF 0%, rgba(238, 242, 255, 0.50) 100%)",
                accentColor: "#6366F1",
                scoreTitle: t("assistants.sleep.scoreTitle", "نمره اختصاصی کیفیت خواب (Sleep Score)"),
                scoreValue: t("assistants.sleep.scoreValue", "۸۹"),
                scoreUnit: t("assistants.sleep.scoreUnit", "از ۱۰۰ (عالی)"),
                centerIconSvg: (
                    <svg viewBox="0 0 48 48" className="w-16 h-16 sm:w-20 sm:h-20 text-white drop-shadow-md" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M39.5 28.5C37.2 35.8 30.5 41 22.5 41C12.8 41 5 33.2 5 23.5C5 15.5 10.2 8.8 17.5 6.5C16.5 9.2 16 12.2 16 15.3C16 26.2 24.8 35 35.7 35C37 35 38.3 34.8 39.5 34.4V28.5Z"
                            fill="currentColor"
                            fillOpacity="0.95"
                        />
                        <path
                            d="M34 11L35.2 14.8L39 16L35.2 17.2L34 21L32.8 17.2L29 16L32.8 14.8L34 11Z"
                            fill="#EEF2FF"
                        />
                        <path
                            d="M23 7L23.8 9.2L26 10L23.8 10.8L23 13L22.2 10.8L20 10L22.2 9.2L23 7Z"
                            fill="#EEF2FF"
                            fillOpacity="0.85"
                        />
                        <path
                            d="M41 23L41.5 24.5L43 25L41.5 25.5L41 27L40.5 25.5L39 25L40.5 24.5L41 23Z"
                            fill="#EEF2FF"
                            fillOpacity="0.85"
                        />
                    </svg>
                ),
                features: [
                    {
                        id: "sl1",
                        title: t("assistants.sleep.features.0.title", "تفکیک مراحل خواب عمیق، سبک و REM"),
                        description: t("assistants.sleep.features.0.description", "نقشه‌برداری زمانی دقیق از چرخه‌های خواب شبانه و بازسازی جسمی و ذهنی"),
                        icon: <BedDouble className="w-5 h-5" />,
                    },
                    {
                        id: "sl2",
                        title: t("assistants.sleep.features.1.title", "محاسبه نمره اختصاصی کیفیت خواب (Sleep Score)"),
                        description: t("assistants.sleep.features.1.description", "ارزیابی جامع مدت، پیوستگی، ضربان و اکسیژن خون در طول استراحت شبانه"),
                        icon: <Sparkles className="w-5 h-5" />,
                    },
                    {
                        id: "sl3",
                        title: t("assistants.sleep.features.2.title", "پایش نظم تنفس در طول خواب"),
                        description: t("assistants.sleep.features.2.description", "شناسایی افت‌های ناگهانی سطح اکسیژن و ناهماهنگی‌های تنفسی حین خواب"),
                        icon: <Wind className="w-5 h-5" />,
                    },
                    {
                        id: "sl4",
                        title: t("assistants.sleep.features.3.title", "بیدارباش هوشمند در سبک‌ترین فاز خواب"),
                        description: t("assistants.sleep.features.3.description", "تنظیم آلارم هوشمند ویبره برای بیدار شدن با نشاط و بدون حس خستگی"),
                        icon: <AlarmClock className="w-5 h-5" />,
                    },
                    {
                        id: "sl5",
                        title: t("assistants.sleep.features.4.title", "رصد دمای پوست در طول خواب"),
                        description: t("assistants.sleep.features.4.description", "بررسی ارتباط افت دمای شبانه با کیفیت ورود به فاز خواب عمیق"),
                        icon: <Thermometer className="w-5 h-5" />,
                    },
                    {
                        id: "sl6",
                        title: t("assistants.sleep.features.5.title", "راهکارهای بهداشت خواب و آرامش شبانه"),
                        description: t("assistants.sleep.features.5.description", "ارائه توصیه‌های علمی بر مبنای ساعت بیولوژیک بدن برای سریع‌تر به خواب رفتن"),
                        icon: <Moon className="w-5 h-5" />,
                    },
                ],
                backDetails: {
                    summary: t("assistants.sleep.backDetails.summary", "دستیار خواب دلبند با آنالیز ریزحرکات و ریتم تنفس، تصویری کامل از بازسازی سلولی و استراحت مغزی ارائه می‌دهد."),
                    metrics: [
                        {
                            label: t("assistants.sleep.backDetails.metrics.0.label", "خواب عمیق (Deep Sleep)"),
                            value: t("assistants.sleep.backDetails.metrics.0.value", "۱h ۵۰m (۲۴٪ کل خواب)"),
                            subtext: t("assistants.sleep.backDetails.metrics.0.subtext", "بازسازی فیزیکی و ترشح هورمون رشد"),
                            status: t("assistants.sleep.backDetails.metrics.0.status", "استاندارد عالی"),
                            statusColor: "#6366F1",
                            progressPercent: 96,
                            icon: <BedDouble className="w-4 h-4 text-indigo-500" />,
                        },
                        {
                            label: t("assistants.sleep.backDetails.metrics.1.label", "فاز رویابینی (REM)"),
                            value: t("assistants.sleep.backDetails.metrics.1.value", "۱h ۳۵m (۲۰٪ کل)"),
                            subtext: t("assistants.sleep.backDetails.metrics.1.subtext", "تثبیت حافظه و تعادل عاطفی"),
                            status: t("assistants.sleep.backDetails.metrics.1.status", "مطلوب و کافی"),
                            statusColor: "#8B5CF6",
                            progressPercent: 82,
                            icon: <Moon className="w-4 h-4 text-purple-500" />,
                        },
                        {
                            label: t("assistants.sleep.backDetails.metrics.2.label", "پایداری و الگوی تنفس"),
                            value: t("assistants.sleep.backDetails.metrics.2.value", "الگوی تنفس منظم (۰ رویداد ناهماهنگ)"),
                            subtext: t("assistants.sleep.backDetails.metrics.2.subtext", "میانگین اکسیژن خون در خواب: ۹۸.۵٪"),
                            status: t("assistants.sleep.backDetails.metrics.2.status", "ریتم بی‌نقص"),
                            statusColor: "#4F46E5",
                            progressPercent: 100,
                            icon: <Wind className="w-4 h-4 text-indigo-600" />,
                        },
                        {
                            label: t("assistants.sleep.backDetails.metrics.3.label", "مدت زمان مفید خواب"),
                            value: t("assistants.sleep.backDetails.metrics.3.value", "۷ ساعت و ۳۵ دقیقه"),
                            subtext: t("assistants.sleep.backDetails.metrics.3.subtext", "زمان به خواب رفتن: ۱۲ دقیقه"),
                            status: t("assistants.sleep.backDetails.metrics.3.status", "کیفیت طلایی"),
                            statusColor: "#7C3AED",
                            progressPercent: 94,
                            icon: <AlarmClock className="w-4 h-4 text-violet-600" />,
                        },
                    ],
                    aiInsight: t("assistants.sleep.backDetails.aiInsight", "دوره خواب عمیق دیشب موجب بازیابی کامل سیستم ایمنی شد. برای تثبیت ریتم سیرکادین، خواب امشب را روی ۲۳:۳۰ تنظیم کنید."),
                    actionLabel: t("assistants.sleep.backDetails.actionLabel", "مشاهده نمودار مراحل خواب"),
                },
            },
        ];
    }, [t]);

    const currentAssistant = assistants.find((a) => a.id === selectedAssistantId) || assistants[0];

    // Auto-select first feature when assistant changes
    const handleAssistantChange = (id: "health" | "women" | "sport" | "sleep") => {
        setSelectedAssistantId(id);
        const nextAssistant = assistants.find((a) => a.id === id);
        if (nextAssistant && nextAssistant.features.length > 0) {
            setActiveFeatureId(nextAssistant.features[0].id);
        }
        setIsFlipped(false);
    };

    return (
        <section
            id="ai-assistant-section"
            className="container relative my-6 sm:my-10 md:my-16 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-10 border border-slate-200/80 bg-gradient-to-b from-slate-50 via-white to-blue-50/20 shadow-sm overflow-hidden"
            dir={isRTL ? "rtl" : "ltr"}
        >
            {/* Ambient subtle background glow */}
            <div
                className="absolute top-0 end-0 w-72 sm:w-96 h-72 sm:h-96 rounded-full blur-3xl pointer-events-none opacity-20 transition-colors duration-700"
                style={{ backgroundColor: currentAssistant.color }}
            />
            <div
                className="absolute bottom-0 start-0 w-72 sm:w-96 h-72 sm:h-96 rounded-full blur-3xl pointer-events-none opacity-15 transition-colors duration-700"
                style={{ backgroundColor: currentAssistant.color }}
            />

            <div className="relative max-w-7xl mx-auto">
                {/* ========================================================================= */}
                {/* ===================== DEDICATED MOBILE VERSION (< lg) =================== */}
                {/* ========================================================================= */}
                <div className="flex flex-col space-y-5 lg:hidden">
                    {/* 1. Mobile Segment Tabs (Horizontal Scrollable with Glassmorphism) */}
                    <div className="relative -mx-1 px-1">
                        <div
                            className="flex items-center gap-2 p-1.5 rounded-2xl overflow-x-auto border border-slate-200/70 backdrop-blur-md bg-slate-100/70"
                            style={{
                                scrollbarWidth: "none",
                                msOverflowStyle: "none",
                            }}
                        >
                            {assistants.map((assistant) => {
                                const isSelected = selectedAssistantId === assistant.id;
                                return (
                                    <button
                                        key={assistant.id}
                                        onClick={() => handleAssistantChange(assistant.id)}
                                        className="relative flex-1 min-w-[100px] py-2.5 px-3 rounded-xl flex items-center justify-center gap-1.5 transition-all duration-200 cursor-pointer shrink-0"
                                        style={{
                                            background: isSelected ? "white" : "transparent",
                                            boxShadow: isSelected
                                                ? `0 3px 12px ${assistant.color}25, 0 1px 2px rgba(0,0,0,0.05)`
                                                : "none",
                                            border: isSelected ? `1px solid ${assistant.color}30` : "1px solid transparent",
                                        }}
                                    >
                                        <div
                                            className="w-4 h-4 flex items-center justify-center shrink-0"
                                            style={{
                                                color: isSelected ? assistant.color : "#94A3B8",
                                            }}
                                        >
                                            {assistant.icon}
                                        </div>
                                        <span
                                            className="text-xs font-bold whitespace-nowrap transition-colors"
                                            style={{
                                                color: isSelected ? "#0F172A" : "#64748B",
                                            }}
                                        >
                                            {assistant.shortTitle}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* 2. Mobile Title & Assistant Header with Biometric Score */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentAssistant.id}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.2 }}
                            className="space-y-3"
                        >
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2.5">
                                    <div
                                        className="w-9 h-9 rounded-xl flex items-center justify-center text-white shadow-sm shrink-0"
                                        style={{ backgroundColor: currentAssistant.color }}
                                    >
                                        {currentAssistant.icon}
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-extrabold text-slate-900 leading-tight">
                                            {currentAssistant.title}
                                        </h2>
                                    </div>
                                </div>

                                {/* Score Pill */}
                                <div
                                    className="px-2.5 py-1 rounded-xl border text-center shrink-0"
                                    style={{
                                        backgroundColor: `${currentAssistant.color}0E`,
                                        borderColor: `${currentAssistant.color}30`,
                                    }}
                                >
                                    <span className="text-[11px] font-black tracking-tight" style={{ color: currentAssistant.color }}>
                                        {currentAssistant.scoreValue} {currentAssistant.scoreUnit}
                                    </span>
                                </div>
                            </div>

                            <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                                {currentAssistant.tagline}
                            </p>
                        </motion.div>
                    </AnimatePresence>

                    {/* 3. Mobile Features List */}
                    <div className="space-y-2 pt-1">
                        <div className="text-xs font-bold text-slate-500 px-1">
                            {t("ui.mobileFeaturesHeading", "قابلیت‌های تخصصی دستیار:")}
                        </div>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={`features-${currentAssistant.id}`}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -6 }}
                                transition={{ duration: 0.25 }}
                                className="flex flex-col gap-2"
                            >
                                {currentAssistant.features.map((feature) => {
                                    const isActive = activeFeatureId === feature.id;
                                    return (
                                        <div
                                            key={feature.id}
                                            onClick={() => setActiveFeatureId(feature.id)}
                                            className={`p-3 rounded-xl cursor-pointer transition-all duration-200 border flex items-start gap-3 ${
                                                isActive
                                                    ? "bg-white shadow-xs"
                                                    : "bg-white/70 border-slate-200/70"
                                            }`}
                                            style={{
                                                borderColor: isActive ? currentAssistant.color : undefined,
                                                boxShadow: isActive ? `0 3px 10px -2px ${currentAssistant.color}20` : undefined,
                                            }}
                                        >
                                            {/* Feature Icon */}
                                            <div
                                                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200 ${
                                                    isActive
                                                        ? `bg-gradient-to-br ${currentAssistant.gradient} text-white`
                                                        : "bg-slate-100 text-slate-700"
                                                }`}
                                                style={{
                                                    color: !isActive ? currentAssistant.color : undefined,
                                                    backgroundColor: !isActive ? `${currentAssistant.color}15` : undefined,
                                                }}
                                            >
                                                {feature.icon}
                                            </div>

                                            {/* Feature Content */}
                                            <div className="flex-1 min-w-0">
                                                <h3
                                                    className="text-xs sm:text-sm font-bold transition-colors duration-200"
                                                    style={{ color: isActive ? currentAssistant.accentColor : "#1e293b" }}
                                                >
                                                    {feature.title}
                                                </h3>
                                                <p className="text-slate-500 text-[11px] mt-0.5 leading-relaxed">
                                                    {feature.description}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>

                {/* ========================================================================= */}
                {/* ===================== DESKTOP VERSION (lg and above) ==================== */}
                {/* ========================================================================= */}
                <div className="hidden lg:grid lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">

                    {/* Left Column: Segment Tabs, Title & Feature Cards Grid */}
                    <div className="lg:col-span-7 flex flex-col justify-between space-y-6">

                        {/* Top Area: Segment Tabs & Heading */}
                        <div className="space-y-4">
                            
                            {/* Segment Tabs Styled EXACTLY like ProductIntro on Home Page */}
                            <div className="relative">
                                <div
                                    className="flex items-center justify-start sm:justify-around gap-1.5 p-1.5 rounded-[20px] overflow-x-auto border border-slate-200/50 backdrop-blur-md"
                                    style={{
                                        background: "rgba(23,31,53,0.04)",
                                        scrollbarWidth: "none",
                                        msOverflowStyle: "none",
                                    }}
                                >
                                    {assistants.map((assistant) => {
                                        const isSelected = selectedAssistantId === assistant.id;
                                        return (
                                            <motion.button
                                                key={assistant.id}
                                                onClick={() => handleAssistantChange(assistant.id)}
                                                className="relative flex-shrink-0 min-w-[130px] sm:min-w-[140px] h-[52px] rounded-xl flex items-center justify-center gap-2.5 px-4 cursor-pointer overflow-hidden transition-all duration-300"
                                                style={{
                                                    background: isSelected
                                                        ? "rgba(255,255,255,0.95)"
                                                        : "transparent",
                                                    border: `1px solid ${isSelected ? "rgba(255,255,255,0.9)" : "transparent"}`,
                                                    backdropFilter: isSelected ? "blur(8px)" : "none",
                                                    WebkitBackdropFilter: isSelected ? "blur(8px)" : "none",
                                                    boxShadow: isSelected
                                                        ? `0 4px 16px rgba(217,217,217,0.5), 0 1px 0 rgba(255,255,255,0.9) inset`
                                                        : "none",
                                                }}
                                                whileHover={{ scale: isSelected ? 1 : 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                            >
                                                {/* Icon when not selected */}
                                                {!isSelected && (
                                                    <div className="relative w-6 h-6 flex items-center justify-center">
                                                        <div
                                                            className="absolute inset-0 w-full h-full flex items-center justify-center"
                                                            style={{
                                                                color: "#8E95A5",
                                                            }}
                                                        >
                                                            {assistant.icon}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Label */}
                                                <span
                                                    className="text-sm sm:text-base font-bold transition-colors duration-200 whitespace-nowrap"
                                                    style={{
                                                        color: isSelected ? "#0F172A" : "#64748B",
                                                    }}
                                                >
                                                    {assistant.shortTitle}
                                                </span>
                                            </motion.button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Main Title & Description with Smooth Transition */}
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentAssistant.id}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.25 }}
                                >
                                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-slate-900 leading-snug mb-2">
                                        {currentAssistant.title}
                                    </h2>

                                    <p className="text-slate-600 text-sm md:text-base leading-relaxed max-w-2xl">
                                        {currentAssistant.tagline}
                                    </p>
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* Feature Cards Grid */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentAssistant.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.3 }}
                                className="grid sm:grid-cols-2 gap-3.5 pt-1"
                            >
                                {currentAssistant.features.map((feature) => {
                                    const isActive = activeFeatureId === feature.id;
                                    return (
                                        <div
                                            key={feature.id}
                                            onClick={() => setActiveFeatureId(feature.id)}
                                            className={`
                                                p-4 rounded-2xl cursor-pointer transition-all duration-200 border flex items-start gap-3.5
                                                ${
                                                    isActive
                                                        ? "bg-white shadow-sm ring-1"
                                                        : "bg-white/80 border-slate-200/80 hover:bg-white hover:border-slate-300"
                                                }
                                            `}
                                            style={{
                                                borderColor: isActive ? currentAssistant.color : undefined,
                                                boxShadow: isActive ? `0 4px 12px -2px ${currentAssistant.color}25` : undefined,
                                            }}
                                        >
                                            {/* Feature Icon */}
                                            <div
                                                className={`
                                                    w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all duration-200
                                                    ${
                                                        isActive
                                                            ? `bg-gradient-to-br ${currentAssistant.gradient} text-white shadow-sm`
                                                            : "bg-slate-100/90 text-slate-700"
                                                    }
                                                `}
                                                style={{
                                                    color: !isActive ? currentAssistant.color : undefined,
                                                    backgroundColor: !isActive ? `${currentAssistant.color}15` : undefined,
                                                }}
                                            >
                                                {feature.icon}
                                            </div>

                                            {/* Feature Content */}
                                            <div className="flex-1 min-w-0">
                                                <h3
                                                    className="text-sm md:text-base font-bold transition-colors duration-200"
                                                    style={{ color: isActive ? currentAssistant.accentColor : "#1e293b" }}
                                                >
                                                    {feature.title}
                                                </h3>
                                                <p className="text-slate-500 text-xs mt-1 leading-relaxed">
                                                    {feature.description}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Right Column: Interactive 3D Flip Card (Desktop Only) */}
                    <div className="lg:col-span-5 h-full relative flex flex-col justify-stretch">
                        <div
                            className="w-full h-full min-h-[490px] lg:min-h-[530px] rounded-3xl border border-slate-200/80 bg-white p-5 sm:p-6 shadow-sm overflow-hidden flex flex-col relative"
                            style={{ perspective: "1200px" }}
                            onMouseEnter={() => setIsFlipped(true)}
                            onMouseLeave={() => setIsFlipped(false)}
                            onClick={() => setIsFlipped((prev) => !prev)}
                        >
                            {/* Inner 3D Flippable Container */}
                            <motion.div
                                className="w-full h-full relative flex-1 flex flex-col"
                                animate={{ rotateY: isFlipped ? 180 : 0 }}
                                transition={{ duration: 0.65, ease: [0.23, 1, 0.32, 1] }}
                                style={{ transformStyle: "preserve-3d" }}
                            >

                                {/* ================= FRONT FACE (Clean & Focused) ================= */}
                                <div
                                    className="w-full h-full flex flex-col justify-between items-center"
                                    style={{
                                        backfaceVisibility: "hidden",
                                        WebkitBackfaceVisibility: "hidden",
                                    }}
                                >
                                    {/* Center Large Concentric Squircle Rings with AI Icon */}
                                    <div className="relative my-auto flex items-center justify-center w-full min-h-[300px]">
                                        
                                        {/* Soft Ambient Background Glow */}
                                        <motion.div
                                            className="absolute w-64 h-64 sm:w-72 sm:h-72 rounded-[3.5rem] blur-2xl pointer-events-none"
                                            style={{
                                                background: `radial-gradient(circle, ${currentAssistant.color}25 0%, ${currentAssistant.color}00 70%)`,
                                            }}
                                            animate={{
                                                scale: [1, 1.15, 1],
                                                opacity: [0.5, 0.85, 0.5],
                                            }}
                                            transition={{
                                                duration: 4,
                                                repeat: Infinity,
                                                ease: "easeInOut",
                                            }}
                                        />

                                        {/* Outer Squircle Ring 3 (Slow rotation & pulse) */}
                                        <motion.div
                                            className="w-72 h-72 sm:w-80 sm:h-80 md:w-88 md:h-88 rounded-[3.2rem] border border-dashed flex items-center justify-center absolute transition-colors duration-500"
                                            style={{
                                                borderColor: `${currentAssistant.color}35`,
                                                backgroundColor: `${currentAssistant.color}06`,
                                            }}
                                            animate={{
                                                rotate: [0, 90, 180, 270, 360],
                                                scale: [1, 1.025, 1],
                                            }}
                                            transition={{
                                                rotate: { duration: 36, repeat: Infinity, ease: "linear" },
                                                scale: { duration: 4.5, repeat: Infinity, ease: "easeInOut" },
                                            }}
                                        >
                                            {/* Satellite Orbit Dot on Outer Squircle */}
                                            <div
                                                className="absolute -top-1.5 w-3 h-3 rounded-full shadow-sm flex items-center justify-center"
                                                style={{ backgroundColor: currentAssistant.color }}
                                            >
                                                <div className="w-1 h-1 rounded-full bg-white animate-ping" />
                                            </div>
                                            <div
                                                className="absolute -bottom-1.5 w-2 h-2 rounded-full opacity-60"
                                                style={{ backgroundColor: currentAssistant.color }}
                                            />
                                        </motion.div>

                                        {/* Middle Squircle Ring 2 (Counter-rotation & gentle scale) */}
                                        <motion.div
                                            className="w-56 h-56 sm:w-64 sm:h-64 rounded-[2.5rem] border flex items-center justify-center absolute transition-colors duration-500"
                                            style={{
                                                borderColor: `${currentAssistant.color}50`,
                                                backgroundColor: `${currentAssistant.color}10`,
                                            }}
                                            animate={{
                                                rotate: [360, 270, 180, 90, 0],
                                                scale: [1, 1.03, 1],
                                            }}
                                            transition={{
                                                rotate: { duration: 28, repeat: Infinity, ease: "linear" },
                                                scale: { duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 },
                                            }}
                                        >
                                            <div
                                                className="absolute -start-1 w-2.5 h-2.5 rounded-full"
                                                style={{ backgroundColor: currentAssistant.color }}
                                            />
                                            <div
                                                className="absolute -end-1 w-1.5 h-1.5 rounded-full opacity-50"
                                                style={{ backgroundColor: currentAssistant.color }}
                                            />
                                        </motion.div>

                                        {/* Inner Squircle Ring 1 (Soft breathing backdrop) */}
                                        <motion.div
                                            className="w-40 h-40 sm:w-48 sm:h-48 rounded-[2rem] border flex items-center justify-center absolute backdrop-blur-xs transition-colors duration-500"
                                            style={{
                                                borderColor: `${currentAssistant.color}70`,
                                                backgroundColor: `${currentAssistant.color}16`,
                                            }}
                                            animate={{
                                                scale: [1, 1.05, 1],
                                            }}
                                            transition={{
                                                duration: 3,
                                                repeat: Infinity,
                                                ease: "easeInOut",
                                            }}
                                        />

                                        {/* Floating Micro-Sparkle Accents (Subtle & refined) */}
                                        <motion.div
                                            className="absolute -top-2 end-6 z-20 bg-white/90 backdrop-blur-md p-1.5 rounded-full shadow-sm border border-slate-100"
                                            style={{ color: currentAssistant.color }}
                                            animate={{ y: [0, -5, 0], rotate: [0, 8, -8, 0] }}
                                            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                                        >
                                            <Sparkles className="w-3.5 h-3.5" />
                                        </motion.div>

                                        <motion.div
                                            className="absolute -bottom-1 start-8 z-20 bg-white/90 backdrop-blur-md p-1.5 rounded-full shadow-sm border border-slate-100"
                                            style={{ color: currentAssistant.color }}
                                            animate={{ y: [0, 5, 0], rotate: [0, -8, 8, 0] }}
                                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                                        >
                                            <Sparkles className="w-3 h-3" />
                                        </motion.div>

                                        {/* Central Big Squircle Avatar Box (Interactive floating container) */}
                                        <motion.div
                                            animate={{
                                                y: [0, -5, 0],
                                            }}
                                            transition={{
                                                duration: 3.5,
                                                repeat: Infinity,
                                                ease: "easeInOut",
                                            }}
                                            whileHover={{
                                                scale: 1.12,
                                                rotate: [0, -2, 2, 0],
                                                transition: { duration: 0.3 },
                                            }}
                                            whileTap={{ scale: 0.95 }}
                                            className={`
                                                relative z-10 w-28 h-28 sm:w-32 sm:h-32 rounded-[1.8rem] bg-gradient-to-br ${currentAssistant.gradient}
                                                shadow-xl flex items-center justify-center p-4 transition-all duration-500 cursor-pointer
                                            `}
                                            style={{
                                                boxShadow: `0 16px 32px -6px ${currentAssistant.color}55`,
                                            }}
                                        >
                                            {currentAssistant.centerIconSvg}
                                        </motion.div>
                                    </div>

                                    {/* Bottom Hint Banner */}
                                    <div className="w-full bg-slate-50/90 rounded-2xl p-3 border border-slate-200/80 flex items-center justify-center gap-2 text-xs font-semibold text-slate-600 transition-all hover:bg-slate-100 hover:text-slate-800 cursor-pointer">
                                        <RotateCw className="w-4 h-4 text-slate-500 animate-spin-slow" />
                                        <span>{t("ui.flipHint", "برای مشاهده جزئیات زنده و پارامترهای تحلیلی هاور یا کلیک کنید")}</span>
                                    </div>
                                </div>

                                {/* ================= BACK FACE (MINIMAL, CLEAN & ELEGANT) ================= */}
                                <div
                                    className="w-full h-full absolute inset-0 flex flex-col justify-between p-3 sm:p-4 bg-white/95"
                                    style={{
                                        backfaceVisibility: "hidden",
                                        WebkitBackfaceVisibility: "hidden",
                                        transform: "rotateY(180deg)",
                                    }}
                                >

                                    {/* Back Header: Clean Title + Score Badge */}
                                    <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="w-10 h-10 rounded-2xl flex items-center justify-center text-white shadow-sm"
                                                style={{ backgroundColor: currentAssistant.color }}
                                            >
                                                {currentAssistant.icon}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h4 className="text-base font-extrabold text-slate-900">
                                                        {currentAssistant.shortTitle}
                                                    </h4>
                                                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                                </div>
                                                <p className="text-xs text-slate-400 font-medium">{t("ui.backHeaderSub", "پایش هوشمند و پیوسته")}</p>
                                            </div>
                                        </div>

                                        {/* Score Chip */}
                                        <div
                                            className="px-3 py-1.5 rounded-xl text-center border"
                                            style={{
                                                backgroundColor: `${currentAssistant.color}0D`,
                                                borderColor: `${currentAssistant.color}25`,
                                            }}
                                        >
                                            <span className="text-xs font-black" style={{ color: currentAssistant.color }}>
                                                {currentAssistant.scoreValue} {currentAssistant.scoreUnit}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Streamlined Metrics (Clean 2x2 Grid without cluttered tags) */}
                                    <div className="grid grid-cols-2 gap-2.5 my-auto">
                                        {currentAssistant.backDetails.metrics.map((metric, idx) => (
                                            <div
                                                key={idx}
                                                className="p-3 rounded-2xl border border-slate-100 bg-slate-50/60 hover:bg-slate-50 transition-colors flex flex-col justify-between"
                                            >
                                                <div className="flex items-center gap-2 text-slate-600 mb-1.5">
                                                    <div
                                                        className="w-6 h-6 rounded-lg flex items-center justify-center text-xs"
                                                        style={{ backgroundColor: `${currentAssistant.color}15` }}
                                                    >
                                                        {metric.icon}
                                                    </div>
                                                    <span className="font-bold text-xs text-slate-700 truncate">{metric.label}</span>
                                                </div>

                                                <div>
                                                    <div
                                                        className="text-base font-extrabold tracking-tight"
                                                        style={{ color: currentAssistant.color }}
                                                    >
                                                        {metric.value}
                                                    </div>
                                                    <div className="text-[11px] text-slate-400 mt-0.5 font-medium truncate">
                                                        {metric.subtext}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* AI Insight Box (Clean & Minimal) */}
                                    <div
                                        className="p-3 rounded-2xl border text-xs leading-relaxed"
                                        style={{
                                            backgroundColor: `${currentAssistant.color}08`,
                                            borderColor: `${currentAssistant.color}20`,
                                        }}
                                    >
                                        <div className="flex items-center gap-1.5 font-bold mb-1" style={{ color: currentAssistant.color }}>
                                            <Sparkles className="w-3.5 h-3.5" />
                                            <span>{t("ui.aiRecommendationLabel", "توصیه هوشمند:")}</span>
                                        </div>
                                        <p className="text-slate-600 text-xs leading-relaxed line-clamp-2">
                                            {currentAssistant.backDetails.aiInsight}
                                        </p>
                                    </div>

                                    {/* Bottom Return & Interactive Action */}
                                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs text-slate-400">
                                        <span className="flex items-center gap-1 text-slate-400 hover:text-slate-700 transition-colors">
                                            <RotateCw className="w-3 h-3" />
                                            <span>{t("ui.flipAgain", "چرخش دوباره")}</span>
                                        </span>

                                        <span
                                            className="font-bold flex items-center gap-1 hover:underline cursor-pointer"
                                            style={{ color: currentAssistant.color }}
                                        >
                                            <span>{currentAssistant.backDetails.actionLabel}</span>
                                            <ArrowUpRight className="w-3.5 h-3.5" />
                                        </span>
                                    </div>
                                </div>

                            </motion.div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default AIAssistantSection;
